import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  InputProps,
  Select,
  SelectProps,
  Text
} from '@chakra-ui/react'
import {
  format,
  isValid,
  lastDayOfMonth,
  setDate,
  setMonth,
  setYear
} from 'date-fns'
import { Controller, FieldValues, useFormContext } from 'react-hook-form'
import { AttentionIcon } from '../Icons/AttentionIcon'
import { CommonFormLabel } from './CommonFormLabel'
import { FieldInputProps, getErrorMessage } from './FormTextInput'

const formatDateToString = (date: Date) => format(date, 'yyyy-MM-dd')

type FormYearSelectProps<T extends FieldValues> = FieldInputProps<T, string> &
  InputProps & {
    /**
     * 選択できる年の範囲を決められる
     * start: 開始
     * end: 終了
     */
    yearProps?: SelectProps & {
      start?: number
      end?: number
      allowEmpty?: boolean
    }
    monthProps?: SelectProps & {
      allowEmpty?: boolean
    }
    dateProps?: SelectProps & {
      allowEmpty?: boolean
    }
  }

const nowYear = new Date().getFullYear()
export const FormYearSelect = <T extends FieldValues = never>({
  name,
  isRequired,
  label,
  labelIndexNumber,
  helperText,
  helpTooltipText,
  showErrorText = true,
  errorMessage,
  formControlWidth,
  yearProps = {
    end: nowYear,
    allowEmpty: true,
    w: '72px'
  },
  monthProps = {
    allowEmpty: true,
    w: '50px'
  },
  dateProps = {
    allowEmpty: true,
    w: '50px'
  }
}: FormYearSelectProps<T>): ReturnType<React.VFC> => {
  const { control } = useFormContext()

  const _errorMessage = getErrorMessage(name) || errorMessage

  return (
    <FormControl
      isRequired={isRequired}
      isInvalid={_errorMessage}
      w={formControlWidth || 'auto'}
    >
      <Controller
        render={({ field: { onChange, value } }) => {
          const dateObject =
            typeof value === 'string'
              ? isValid(new Date(value))
                ? new Date(value)
                : undefined
              : undefined
          const year = dateObject ? dateObject.getFullYear() : undefined
          // monthは0...11の範囲を取るので注意
          const month = dateObject ? dateObject.getMonth() : undefined
          const date = dateObject ? dateObject.getDate() : undefined
          const {
            start = nowYear - 100,
            end = nowYear,
            ...yearPropsWithoutStart
          } = yearProps

          return (
            <Box>
              <CommonFormLabel
                label={label}
                labelIndexNumber={labelIndexNumber}
                helpTooltipText={helpTooltipText}
                isRequired={isRequired}
              />
              <Flex gap="6px" fontSize="13px" fontWeight={400} align="center">
                <Select
                  onChange={(e) =>
                    onChange(
                      formatDateToString(
                        setYear(
                          dateObject || new Date(),
                          Number(e.target.value)
                        )
                      )
                    )
                  }
                  value={year}
                  w="72px"
                  placeholder={yearProps.allowEmpty === false ? undefined : ' '}
                  {...yearPropsWithoutStart}
                >
                  {[...Array(start ? end - start + 1 : 100)].map((_, i) => (
                    <option value={start + i} key={i}>
                      {start + i}
                    </option>
                  ))}
                </Select>
                <Text>年</Text>
                <Select
                  w="54px"
                  placeholder={
                    monthProps.allowEmpty === false ? undefined : ' '
                  }
                  onChange={(e) =>
                    onChange(
                      formatDateToString(
                        setMonth(
                          dateObject || new Date(),
                          // monthは0...11の範囲を取るので注意
                          Number(e.target.value)
                        )
                      )
                    )
                  }
                  value={month}
                  {...monthProps}
                >
                  {[...Array(12)].map((_, i) => (
                    // monthは0...11の範囲を取るので注意
                    <option value={i} key={i}>
                      {i + 1}
                    </option>
                  ))}
                </Select>
                <Text>月</Text>

                <Select
                  w="54px"
                  placeholder={dateProps.allowEmpty === false ? undefined : ' '}
                  onChange={(e) =>
                    onChange(
                      formatDateToString(
                        setDate(
                          dateObject || new Date(),
                          Number(e.target.value)
                        )
                      )
                    )
                  }
                  value={date}
                  {...dateProps}
                >
                  {[
                    ...Array(lastDayOfMonth(dateObject || new Date()).getDate())
                  ].map((_, i) => (
                    <option value={i + 1} key={i}>
                      {i + 1}
                    </option>
                  ))}
                </Select>
                <Text>日</Text>
              </Flex>
            </Box>
          )
        }}
        control={control}
        name={name}
      />

      {showErrorText && (
        <FormErrorMessage>
          <AttentionIcon />
          {_errorMessage}
        </FormErrorMessage>
      )}
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  )
}
